import React, { useEffect, useState, useRef } from 'react';
import { useFormik } from 'formik';
import {
  Button,
  DatePicker,
  DatePickerInput,
  TextInput,
  Form,
  InlineLoading
} from 'carbon-components-react';
import { EnquiryEditFormValues } from 'types';
import { FormError } from 'components';
import { EditEnquiryDetailsSchema } from 'utilities/validation/editEnquiryDetails.validation';
import Utils from 'utilities/helper.util';

const EditForm: React.FC<Props> = ({
  onClose,
  onSaveEnquiry,
  updating,
  enquiryData,
  calendarRef
}) => {
  const {
    touched,
    errors,
    values,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldTouched,
    setValues
  } = useFormik<EnquiryEditFormValues>({
    enableReinitialize: true,
    initialValues: { userReference: '', expireAt: '' },
    validationSchema: EditEnquiryDetailsSchema,
    onSubmit: (values: EnquiryEditFormValues) => {
      waitingSubmitRef.current = true;
      onSaveEnquiry(values);
    }
  });

  const [savedSuccess, setSavedSuccess] = useState<boolean>(false);
  const waitingSubmitRef = useRef<boolean>(false);

  const onCalendarClose = (val: any, strValue: any, self: any) => {
    strValue && self.setDate(strValue, true, 'd/m/Y');
  };

  const handleExpireAtChange = (value: any) => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        const updatedValue = value[0];
        const localTime = Utils.getNewDate(
          values.expireAt || enquiryData.expire_at
        )
          .local()
          .format('THH:mm:ssZ');
        const localDate = Utils.getNewDate(updatedValue).format('YYYY-MM-DD');
        const newDate = Utils.getNewDate(`${localDate}${localTime}`)
          .utc()
          .format();
        setFieldValue('expireAt', newDate);
        setFieldTouched('expireAt', true, true);
      } else {
        setFieldValue('expireAt', '');
        setFieldTouched('expireAt', true, true);
      }
    }
  };

  useEffect(() => {
    if (enquiryData) {
      setValues({
        userReference: enquiryData.user_reference || '',
        expireAt: enquiryData.expire_at || ''
      });
    }
  }, [enquiryData, setValues]);

  useEffect(() => {
    if (!updating && waitingSubmitRef.current) {
      setSavedSuccess(true);
      waitingSubmitRef.current = false;
      setTimeout(() => setSavedSuccess(false), 1500);
    }
  }, [updating, onClose]);

  let daysToAdd = 2;
  if (enquiryData?.is_edit && enquiryData?.auto_renew_edited) {
    daysToAdd = enquiryData.auto_renew_every_edited + 1;
  } else if (!enquiryData?.is_edit && enquiryData?.auto_renew) {
    daysToAdd = enquiryData.auto_renew_every + 1;
  }

  const expiredMinDate = Utils.toLocalDateFormat(
    Utils.getNewDate().add(daysToAdd, 'day')
  );
  const expiredMaxDate = enquiryData?.enquiry_at
    ? Utils.toLocalDateFormat(
        Utils.getNewDate(enquiryData.enquiry_at).add(28, 'day')
      )
    : undefined;

  return (
    <div className="enquiry-edit-form-container">
      <div className="enquiry-edit-form">
        <div className="enquiry-edit-form-title">Enquiry properties</div>
        <Form className="modal-form--wrapper" onSubmit={handleSubmit}>
          <div className="enquiry-edit-form-group">
            <TextInput
              id="userReference"
              name="userReference"
              labelText="User reference"
              placeholder="Brief reference to help identify this job"
              data-testid="userReference"
              maxLength={50}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.userReference}
            />
            {errors.userReference && touched.userReference && (
              <FormError>{errors.userReference}</FormError>
            )}
          </div>
          <div className="enquiry-edit-form-group">
            {!enquiryData?.is_expired && enquiryData?.internal_create ? (
              <>
                <DatePicker
                  dateFormat="d/m/Y"
                  datePickerType="single"
                  value={
                    values.expireAt
                      ? Utils.toLocalDateFormat(values.expireAt)
                      : ''
                  }
                  onChange={handleExpireAtChange}
                  onClose={onCalendarClose}
                  minDate={expiredMinDate}
                  maxDate={expiredMaxDate}
                  ref={calendarRef}
                >
                  <DatePickerInput
                    id="enquiryExpireAt"
                    name="expireAt"
                    data-testid="enquiryExpireAt"
                    labelText="Expiry date"
                    placeholder="dd/mm/yyyy"
                    size={'md'}
                    type="text"
                    autoComplete="off"
                  />
                </DatePicker>
                {errors.expireAt && touched.expireAt && (
                  <FormError>{errors.expireAt}</FormError>
                )}
              </>
            ) : (
              <div className="disabled-expire-at-input">
                <TextInput
                  id="enquiryExpireAt"
                  name="expireAt"
                  data-testid="enquiryExpireAt"
                  labelText="Expiry date"
                  value={
                    values.expireAt
                      ? Utils.toLocalDateFormat(values.expireAt)
                      : ''
                  }
                  readOnly={true}
                />
              </div>
            )}
          </div>
          <div className="enquiry-edit-form-buttons enquiry-edit-form-group">
            <Button
              id="cancelEditEnquiry"
              kind="tertiary"
              size="field"
              onClick={() => onClose()}
            >
              Back
            </Button>
            <Button
              id="submitEditEnquiry"
              kind="secondary"
              size="field"
              type="submit"
              data-testid="submitEditEnquiry"
              disabled={
                (!touched.expireAt && !touched.userReference) ||
                savedSuccess ||
                updating
              }
            >
              {!updating && !savedSuccess ? (
                'Apply'
              ) : (
                <InlineLoading
                  description={savedSuccess ? 'Saved' : 'Saving...'}
                  status={savedSuccess ? 'finished' : 'active'}
                  aria-live={'assertive'}
                />
              )}
            </Button>
          </div>
        </Form>
      </div>
      <div className="enquiry-edit-form-footer">
        Enquiries should be set to expire based on the earliest plan expiry date
        (usually 28 days), but some asset owners set a shorter validity like 14
        days.
      </div>
    </div>
  );
};

type Props = {
  onClose: Function;
  onSaveEnquiry: Function;
  enquiryData: any;
  updating: boolean;
  error?: string;
  calendarRef: React.RefObject<DatePicker>;
};

export default React.memo(EditForm);
